import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { DEFAULT_LOCALE, isLocale, type Locale } from '@nk/shared';
import App from '@/App';
import { ROUTE_DATA_GLOBAL, type RouteData } from '@/lib/route-data';
import { setOrigin } from '@/lib/dom';
import '@/styles.css';

/**
 * The locale is read from `<html lang>` rather than from a global the server injects, so
 * the client cannot disagree with the document it is hydrating.
 */
function activeLocale(): Locale {
  const lang = document.documentElement.lang.split('-')[0];
  return isLocale(lang) ? lang : DEFAULT_LOCALE;
}

/**
 * The data the server already rendered with. Reading it here — rather than refetching —
 * is what keeps hydration from throwing away a homepage the reader can already see.
 */
function initialData(): RouteData {
  const raw = (window as unknown as Record<string, unknown>)[ROUTE_DATA_GLOBAL];
  return (raw as RouteData | undefined) ?? {};
}

setOrigin(window.location.origin);

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from the document');

const tree = (
  <StrictMode>
    <BrowserRouter>
      <App locale={activeLocale()} initialData={initialData()} />
    </BrowserRouter>
  </StrictMode>
);

/**
 * Public pages are server-rendered and hydrated; `/admin/*` is deliberately not
 * server-rendered (§1.1), so its document arrives with an empty root.
 *
 * Hydrating an empty root is not free: React finds nothing to match, logs a hydration
 * mismatch and recovers by re-rendering the whole tree client-side. It worked, which is
 * why it went unnoticed until the CMS had something to draw — the console on every admin
 * page load carried two React errors. An empty root gets `createRoot`, which is what it
 * always was.
 */
if (root.firstElementChild) hydrateRoot(root, tree);
else createRoot(root).render(tree);
